/**
 * Shopping cart state management using localStorage.
 *
 * The cart is also mirrored to the backend (POST /api/cart/sync, debounced) so
 * the store can report on abandoned carts and email a recovery link. The mirror
 * is keyed on the tracker's persistent visitor id (`tt_vid`); a guest can attach
 * an email (`ab8beauty_cart_email`) so their cart is recoverable too. Sync is
 * best-effort and never blocks the UI.
 */
import { useState, useCallback, useEffect, useRef } from 'react';

const CART_KEY = 'ab8beauty_cart';
const COUPON_KEY = 'ab8beauty_coupon';
const CART_EMAIL_KEY = 'ab8beauty_cart_email';

const SYNC_URL = (process.env.REACT_APP_API_URL || '/api') + '/cart/sync';
const SYNC_DEBOUNCE_MS = 1500;

const loadCart = () => {
  try {
    const c = localStorage.getItem(CART_KEY);
    return c ? JSON.parse(c) : [];
  } catch { return []; }
};

const saveCart = (cart) => localStorage.setItem(CART_KEY, JSON.stringify(cart));

const loadCoupon = () => {
  try {
    const c = localStorage.getItem(COUPON_KEY);
    return c ? JSON.parse(c) : null;
  } catch { return null; }
};

const loadCartEmail = () => {
  try { return localStorage.getItem(CART_EMAIL_KEY) || ''; } catch { return ''; }
};

// The tracker's persistent per-browser id (localStorage `tt_vid`). Reused here so
// a shopper's cart mirror is stable across visits and matches their analytics.
const uuid = () => (
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      })
);
const getVisitorId = () => {
  try {
    let v = localStorage.getItem('tt_vid');
    if (!v) { v = uuid(); localStorage.setItem('tt_vid', v); }
    return v;
  } catch { return uuid(); }
};

// Fire-and-forget mirror to the backend. Sends the bearer token by hand (like
// AnalyticsTracker) so logged-in carts get attributed without the axios
// interceptor's 401→login redirect ever firing on a cart sync.
const postSync = (items, email) => {
  try {
    const headers = { 'Content-Type': 'application/json' };
    let token = null;
    try { token = localStorage.getItem('accessToken'); } catch {}
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(SYNC_URL, {
      method: 'POST',
      headers,
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({ visitorId: getVisitorId(), items, email: email || undefined }),
    }).catch(() => {});
  } catch { /* never disrupt the cart over a sync */ }
};

export const useCart = () => {
  const [items, setItems] = useState(loadCart);
  // The coupon validated against the backend in the cart, persisted so it
  // carries over to checkout (and survives reloads). Shape mirrors the
  // /orders/validate-coupon response: { code, description, discountInCents, freeShipping }.
  const [coupon, setCouponState] = useState(loadCoupon);
  // Guest-entered email used to make an anonymous cart recoverable.
  const [cartEmail, setCartEmailState] = useState(loadCartEmail);

  const setCoupon = useCallback((c) => {
    setCouponState(c);
    try {
      if (c) localStorage.setItem(COUPON_KEY, JSON.stringify(c));
      else localStorage.removeItem(COUPON_KEY);
    } catch { /* ignore quota / disabled storage */ }
  }, []);

  const clearCoupon = useCallback(() => setCoupon(null), [setCoupon]);

  const setCartEmail = useCallback((email) => {
    const v = (email || '').trim();
    setCartEmailState(v);
    try {
      if (v) localStorage.setItem(CART_EMAIL_KEY, v);
      else localStorage.removeItem(CART_EMAIL_KEY);
    } catch { /* ignore */ }
  }, []);

  const addItem = useCallback((variant, product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.variantId === variant.id);
      const next = existing
        ? prev.map(i => i.variantId === variant.id
            ? { ...i, quantity: i.quantity + quantity } : i)
        : [...prev, {
            variantId: variant.id,
            productName: product.name,
            variantName: variant.name,
            priceInCents: variant.priceInCents,
            imageUrl: product.imageUrl,
            quantity,
          }];
      saveCart(next);
      return next;
    });
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems(prev => {
      const next = prev.filter(i => i.variantId !== variantId);
      saveCart(next);
      return next;
    });
  }, []);

  const updateQuantity = useCallback((variantId, quantity) => {
    if (quantity < 1) return;
    setItems(prev => {
      const next = prev.map(i => i.variantId === variantId ? { ...i, quantity } : i);
      saveCart(next);
      return next;
    });
  }, []);

  // Replace the whole cart (used by the recovery-link page to rebuild a cart
  // from the server-stored snapshot).
  const replaceCart = useCallback((nextItems) => {
    const next = Array.isArray(nextItems) ? nextItems : [];
    setItems(next);
    saveCart(next);
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_KEY);
    setCoupon(null);
  }, [setCoupon]);

  // ── Debounced backend mirror ──────────────────────────────────────────────
  // Coalesces rapid edits (qty steppers, add/remove). Skips brand-new empty
  // carts so we don't create a row for every visitor; once a cart has been
  // synced we keep pushing (so emptying it server-side clears the abandonment).
  const syncTimer = useRef(null);
  const hasSynced = useRef(false);
  useEffect(() => {
    if (items.length === 0 && !hasSynced.current && !cartEmail) return;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      postSync(items, cartEmail);
      hasSynced.current = true;
    }, SYNC_DEBOUNCE_MS);
    return () => { if (syncTimer.current) clearTimeout(syncTimer.current); };
  }, [items, cartEmail]);

  const total = items.reduce((s, i) => s + i.priceInCents * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return { items, addItem, removeItem, updateQuantity, replaceCart, clearCart, total, count,
           coupon, setCoupon, clearCoupon,
           cartEmail, setCartEmail };
};
